import { useEffect, useRef, useState, type JSX } from 'react';

import { useInterval } from '@admin-platform/ui/components';
import classNames from 'classnames';

import LoadingIndicator from '../LoadingIndicator/LoadingIndicator';

import styles from './LoadingOverlay.module.css';

// Witty, on-brand status lines — calm and confident, no exclamation, no emoji.
const MESSAGES = [
  'Tuning the revenue signal',
  'Counting every recurring dollar',
  'Doing the subscription math',
  'Reading the revenue waveform',
  'Composing your pricing',
];

// The boot sequence renders this overlay from three separate gates (auth →
// tenant → onboarding) that sit at different depths in the tree, so React
// unmounts and remounts it as each gate resolves. Persisting the rotating
// message index and the "already entered" flag at module scope (they reset on a
// real page reload, when the module re-evaluates) lets the remounted overlay
// pick up where the previous one left off instead of restarting — which is what
// made the splash flash. Both reset on a genuine reload.
let persistedIndex = 0;
let hasEnteredOnce = false;

export default function LoadingOverlay(): JSX.Element {
  const [index, setIndex] = useState(persistedIndex);

  // Play the entrance animation only on the first mount of this page load; on
  // the gate hand-off remounts the overlay should just stay put.
  const playEntranceRef = useRef(!hasEnteredOnce);
  // The message index this mount started at, so we fade only genuine rotations
  // and not the initial render of a remounted overlay.
  const startIndexRef = useRef(index);

  useEffect(() => {
    hasEnteredOnce = true;
  }, []);

  useInterval(() => {
    setIndex((current) => {
      const next = (current + 1) % MESSAGES.length;
      persistedIndex = next;
      return next;
    });
  }, 2400);

  const animateMessage = index !== startIndexRef.current;

  return (
    <div className={styles.LoadingOverlay} role="status" aria-label="Loading">
      <div className={classNames(styles.Stage, { [styles.StageIn]: playEntranceRef.current })}>
        <LoadingIndicator />
        <p
          key={index}
          className={classNames(styles.Message, { [styles.MessageIn]: animateMessage })}
          aria-hidden="true"
        >
          {MESSAGES[index]}
        </p>
      </div>
    </div>
  );
}
